import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Printer, Search, ShieldAlert, Layers, FolderKanban, Hammer, FileCode2,
  BadgeCheck, GitCompareArrows, ShieldCheck, Map, Activity, Mountain, FileStack,
  CheckCircle2, Wrench, ArrowUpRight, Lightbulb, CreditCard,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const GOLDEN_RULES = [
  "Survey data is the source of truth — never move or snap survey to design.",
  "TIN surfaces are reference only — used for levels & volumes, never written to ADAC output.",
  "Tolerances are pass/fail criteria, not rounding allowances — don't edit survey values to 'fit'.",
  "Always confirm the datum (GDA2020/MGA zone, AHD) of the chosen standard matches the project.",
  "Every tool is usable on the free plan; only PDF exports are metered (3) and QA is locked to your chosen LGA schema.",
];

const PLAN_POINTS = [
  { h: "Free (Starter)", p: "Full access to every tool — Library, Builder, Reader, Validate, Compare, QA, Map, Profile, Surfaces and Projects. Limited to ONE LGA / authority schema (chosen in Setup) and 3 PDF exports in total." },
  { h: "Your LGA schema", p: "On the free plan, open Setup and pick your local authority schema. QA is then locked to that authority. Subscribe to unlock all authorities." },
  { h: "Exports (the 3 free)", p: "Each PDF you generate — QA report, Submission Pack, Validation report or Long-section — counts as one export. The sidebar 'Free exports X/3' meter tracks this; a nudge appears when 1 remains. The 4th prompts you to subscribe." },
  { h: "Subscribing", p: "Open Plans (sidebar 'View plans' or /pricing): Single Authority (A$29/mo · A$290/yr) for one authority, or All Authorities (A$49/mo · A$490/yr). Monthly or annual; prices ex-GST. Enterprise & Council are by contact (hello@cbkm.au)." },
  { h: "Beta access", p: "Beta testers open the invite link (…/?beta=CBKM-BETA-2026) then register — they're granted full access for 365 days, no payment." },
];

const MODULES = [
  {
    id: "setup", code: "WI-1", title: "Setup — schema version & your LGA", to: "/settings", icon: BadgeCheck,
    blurb: "Set the ADAC schema version, default datum and (free plan) your LGA schema.",
    steps: [
      "Open Setup and choose the ADAC schema version used across the app (e.g. 4.1 / 5.0 / 6.0).",
      "Optionally set the default datum (GDA2020/MGA zone, AHD).",
      "Free plan: pick your LGA / authority schema — QA will be locked to it. Save.",
      "Subscribe (Plans) to remove the single-authority restriction.",
    ],
  },
  {
    id: "library", code: "WI-2", title: "Import & manage XML", to: "/", icon: Layers,
    blurb: "Upload, alias and organise your ADAC files.",
    steps: [
      "Open Library and drag-drop or browse to upload one or more .xml ADAC files.",
      "Each file shows filename, asset count and upload time.",
      "Set an alias to make a file easy to recognise in dropdowns.",
      "Use Load samples to populate demo data for training.",
      "Delete obsolete files with the trash action.",
    ],
  },
  {
    id: "builder", code: "WI-3", title: "Create / digitise ADAC XML", to: "/builder", icon: Hammer,
    blurb: "Draw a network on the map and export it to ADAC XML.",
    steps: [
      "Pick an asset type from the legend, then drag it onto the map to start placing assets.",
      "Set the pit/line naming convention so sequential assets auto-number as you draw (Fast naming).",
      "Hold Shift to inherit the previous asset's attributes (Shift-lock inheritance).",
      "Right-click ends a line/polygon.",
      "Use Global shift to translate the whole network onto correct coordinates.",
      "Export to ADAC XML — it appears in the Library.",
    ],
  },
  {
    id: "reader", code: "WI-4", title: "Inspect a file", to: "/reader", icon: FileCode2,
    blurb: "Browse assets, attributes and raw XML before QA.",
    steps: [
      "Open Reader and choose a file.",
      "Browse assets by type; review attributes and raw XML to confirm structure before QA.",
    ],
  },
  {
    id: "validate", code: "WI-5", title: "Validate against the schema", to: "/validate", icon: BadgeCheck, exp: true,
    blurb: "Catch missing mandatory fields and schema breaches.",
    steps: [
      "Open Validate and select the as-constructed file.",
      "Review the summary: missing mandatory fields, malformed values, schema breaches.",
      "Fix issues at source (or via Builder/Profile edit) and re-validate until clean.",
      "Download the Validation PDF (counts as one free export).",
    ],
  },
  {
    id: "compare", code: "WI-6", title: "Compare design vs as-constructed", to: "/compare", icon: GitCompareArrows,
    blurb: "See added / removed / changed assets and field-level diffs.",
    steps: [
      "Open Compare and select the Design and Survey files.",
      "Review added/removed/changed assets and field-level differences to understand scope of change.",
    ],
  },
  {
    id: "qa", code: "WI-7", title: "QA Tolerance assessment", to: "/qa", icon: ShieldCheck, core: true, exp: true,
    blurb: "The core check — survey vs design against an authority standard.",
    steps: [
      "Select the Design and As-Constructed (same project) files.",
      "Choose the Tolerance Standard (free plan: locked to your LGA schema), then View Tolerances to confirm datum & limits.",
      "Leave Auto-accept high-confidence ON (default ≥70%) to link obvious matches, or adjust the slider.",
      "Click Run QA — review the verdict (CONFORMING / NON-CONFORMING), conformance %, and per-asset checks.",
      "Manually link any unmatched MISSING/EXTRA assets; saved supplier-ID rules apply automatically.",
      "Save Report (History) and/or generate the QA PDF (counts as one free export).",
    ],
    extras: [
      { h: "Per-asset-type tolerances", p: "Turn on when different classes are assessed by different bodies (Sewer→Unitywater, Stormwater→Council, Transport→TMR). Set a default fallback, map each category, optionally force per-type overrides. The result shows a per-category conformance table. (Multiple authorities require a subscription.)" },
      { h: "Tolerance profiles", p: "Configure a mapping, Save profile…, name it, and reload it later to re-apply the default + category/type mapping in one click." },
    ],
  },
  {
    id: "map", code: "WI-8", title: "Map viewer & bulk linking", to: "/map", icon: Map,
    blurb: "Reproject MGA/GDA → WGS84 and link assets in bulk.",
    steps: [
      "Open Map — assets reproject from MGA/GDA to WGS84 automatically.",
      "Use bulk linking: auto-accept high-confidence matches, click-to-link the rest, define supplier ID rules for repeat runs.",
      "Confirm survey assets are not moved during linking.",
    ],
  },
  {
    id: "profile", code: "WI-9", title: "Long-sections, cross-sections & design-vs-as-constructed", to: "/profile", icon: Activity, exp: true,
    blurb: "Chain pipes into long-sections, overlay the paired string, check IL conformance, cut cross-sections, edit inverts.",
    steps: [
      "Select a file, Auto-detect strings then pick one — or chain line assets (Shift-select for multiple).",
      "Adjust vertical exaggeration to read grades.",
      "Optionally choose an Existing/Design TIN to draw ground levels and populate graticule rows.",
      "Compare design vs as-constructed: in the 'Compare against' panel choose the other file, then either Pick string (Detect strings and click the matching one) or Corridor auto (set a width to auto-find it within that buffer). The comparison invert overlays as an amber dashed line.",
      "Set the Conformance standard (vertical tolerance) — free plan locks to your LGA schema. The panel shows avg/max ΔIL and how many nodes are within ±tolerance; the graticule gains a 'Δ IL (D−AC)' row (green within tol / red over) and a 'Conform ±Xm' OK/OVER row.",
      "Cross-sections: choose a pit/node, set width & interval, Generate, then export CSV.",
      "Inline invert editing: toggle Edit inverts ON, change a US/DS IL, press Enter to write it back. Only to correct genuine errors.",
      "Raw-XML diff: see exactly which XML lines changed vs the original.",
      "Export the long-section to PDF — it includes the overlaid comparison line, the ΔIL summary, and the per-node 'Δ IL' and 'Conform ±Xm' (OK/OVER) graticule rows (counts as one free export).",
    ],
  },
  {
    id: "surfaces", code: "WI-10", title: "TIN surfaces & earthworks volumes", to: "/surfaces", icon: Mountain,
    blurb: "Import TINs and compute cut/fill volumes.",
    steps: [
      "Import a TIN from LandXML / 12da / XYZ-CSV; set its role (Existing / Design).",
      "TIN-to-TIN volumes: pick Base (lower/existing) and Top (upper/design), set grid spacing, Compute.",
      "Review Cut / Fill / Net / Plan area, the heatmap, and export CSV.",
      "Surfaces are reference only and never exported to ADAC.",
    ],
  },
  {
    id: "projects", code: "WI-11", title: "Projects (workspace record)", to: "/projects", icon: FolderKanban,
    blurb: "One record of the files, standard and surfaces used.",
    steps: [
      "Open Projects → New Project. Enter a name (only the name is mandatory).",
      "Optionally attach the authority standard, design & survey XML, existing/design TINs and notes.",
      "Set a project Active to auto-load its files across all pages.",
      "The project card becomes the single record of what was used for the submission.",
    ],
  },
  {
    id: "submission", code: "WI-12", title: "Generate the Submission Pack", to: "/qa", icon: FileStack, core: true, exp: true,
    blurb: "The council-ready deliverable, bundled into one PDF.",
    steps: [
      "In QA Tolerance, after running QA, optionally choose Pack volume base/top surfaces to append an earthworks page.",
      "Click Submission Pack PDF — it bundles the QA/change report, schema validation, pipe long-sections and cut/fill summary (counts as one free export).",
      "The QA Reviewer checks the pack, confirms CONFORMING, and issues it to the assessing body.",
    ],
  },
];

const CHECKPOINTS = [
  ["Setup", "Correct schema version set; free plan LGA schema chosen"],
  ["Validate", "No mandatory-field or schema errors remaining"],
  ["QA", "Verdict CONFORMING; all MISSING/EXTRA resolved or explained"],
  ["Standard", "Correct authority + datum confirmed via View Tolerances"],
  ["Survey integrity", "No survey coordinate moved/snapped to design"],
  ["Long-section IL", "Design vs as-constructed ΔIL within ±vertical tolerance (Conform row all OK)"],
  ["Surfaces", "TINs used for levels/volumes only; absent from ADAC output"],
  ["Submission Pack", "All sections present and legible; reviewer signed"],
];

const TROUBLESHOOTING = [
  ["Tolerance Standard dropdown is locked", "Free plan — QA is limited to your LGA schema. Change it in Setup, or subscribe (Plans) for all authorities."],
  ["\"You've used all 3 free exports\"", "The free plan allows 3 PDF exports total. Subscribe to a plan for unlimited exports."],
  ["Cross-section / paired string says \"no plan coordinates\"", "The pipes lack Easting/Northing — they must link to pits (US_Pit/DS_Pit) that carry coordinates."],
  ["Volumes \"do not overlap in plan\"", "The two surfaces don't share a footprint — check datum/zone."],
  ["Asset classified as Other unexpectedly", "Type/tag wording not recognised — use a Per-type override in QA."],
  ["Many MISSING/EXTRA in QA", "Lower auto-accept confidence or add supplier ID-mapping rules, then manually link."],
];

const SECTIONS = [
  { id: "account", label: "Account & plans" },
  { id: "rules", label: "Golden rules" },
  { id: "workflow", label: "Recommended workflow" },
  { id: "modules", label: "Module guides" },
  { id: "checkpoints", label: "Sign-off checkpoints" },
  { id: "troubleshooting", label: "Troubleshooting" },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function HelpPage() {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!query) return MODULES;
    return MODULES.filter((m) =>
      [m.title, m.code, m.blurb, ...m.steps, ...(m.extras || []).flatMap((e) => [e.h, e.p])]
        .join(" ").toLowerCase().includes(query)
    );
  }, [query]);

  return (
    <div data-testid="help-page">
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-20 print:hidden">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-blue-400" />
          <div>
            <h1 className="font-display text-xl font-bold tracking-tight">Help & Work Instruction</h1>
            <p className="font-mono text-[11px] text-zinc-500">WI-ADAC-001 · Rev 1.0 · As-constructed QA & submission</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="help-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the guide…"
              className="h-9 w-56 pl-8 bg-[#0C0C0C] border-zinc-800 rounded-sm text-sm"
            />
          </div>
          <button
            data-testid="help-print-btn"
            onClick={() => window.print()}
            className="flex items-center gap-2 h-9 px-4 rounded-sm bg-blue-600 hover:bg-blue-500 text-white font-mono text-[11px] uppercase tracking-wider transition-colors duration-75"
          >
            <Printer className="w-3.5 h-3.5" /> Save as PDF
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-8 py-10 flex gap-10">
        {/* In-page nav */}
        <nav className="hidden lg:block w-44 shrink-0 print:hidden">
          <div className="sticky top-24 space-y-1">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mb-2 px-2">On this page</div>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className="w-full text-left px-2 py-1.5 rounded-sm text-sm text-zinc-400 hover:text-blue-400 hover:bg-zinc-900 transition-colors duration-75"
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex-1 min-w-0 space-y-12">
          {/* Intro */}
          <section className="space-y-3">
            <p className="text-base text-zinc-300 leading-relaxed max-w-3xl">
              The ADAC Suite imports, builds, validates, compares and quality-assures ADAC asset data —
              assessing survey-vs-design tolerances against an authority standard, producing long-sections,
              cross-sections and earthworks volumes, and generating a council-ready Submission Pack.
            </p>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-3xl">
              Every tool is available on the free plan. The free plan is limited to <span className="text-zinc-200">one LGA / authority schema</span> (set in Setup) and <span className="text-zinc-200">3 PDF exports</span> in total; subscribe to unlock all authorities and unlimited exports.
            </p>
            <div className="flex items-start gap-2 text-sm text-zinc-500 max-w-3xl">
              <Lightbulb className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
              <span>New here? Set your schema in <button onClick={() => scrollTo("modules")} className="text-blue-400 hover:underline">Setup</button>, then follow the <button onClick={() => scrollTo("workflow")} className="text-blue-400 hover:underline">recommended workflow</button> top-to-bottom. Each module card links straight to that tool.</span>
            </div>
          </section>

          {/* Account & plans */}
          <section id="account" className="scroll-mt-24">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-blue-400" />
              <h2 className="font-display text-lg font-bold tracking-tight">Account & plans</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {PLAN_POINTS.map((pt) => (
                <div key={pt.h} className="rounded-sm border border-zinc-800 bg-[#0C0C0C] p-5">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-blue-400 mb-1.5">{pt.h}</div>
                  <p className="text-sm text-zinc-300 leading-relaxed">{pt.p}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Golden rules */}
          <section id="rules" className="scroll-mt-24">
            <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldAlert className="w-5 h-5 text-amber-400" />
                <h2 className="font-display text-lg font-bold tracking-tight">Golden rules — read first</h2>
              </div>
              <ol className="space-y-2.5">
                {GOLDEN_RULES.map((r, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-300">
                    <span className="font-mono text-amber-400 text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Workflow */}
          <section id="workflow" className="scroll-mt-24">
            <h2 className="font-display text-lg font-bold tracking-tight mb-5">Recommended workflow</h2>
            <div className="flex flex-wrap items-center gap-2">
              {["Setup", "Library", "Validate", "QA Tolerance", "Profile", "Surfaces", "Submission Pack"].map((step, i, a) => (
                <div key={step} className="flex items-center gap-2">
                  <span className="px-3 py-1.5 rounded-sm bg-zinc-900 border border-zinc-800 font-mono text-[11px] text-zinc-300">
                    <span className="text-blue-400 mr-1.5">{i + 1}</span>{step}
                  </span>
                  {i < a.length - 1 && <span className="text-zinc-700">→</span>}
                </div>
              ))}
            </div>
          </section>

          {/* Modules */}
          <section id="modules" className="scroll-mt-24">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="font-display text-lg font-bold tracking-tight">Module guides</h2>
              {query && <span className="font-mono text-[11px] text-zinc-500">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>}
            </div>
            <div className="grid gap-4">
              {filtered.map((m) => (
                <article
                  key={m.id}
                  data-testid={`help-module-${m.id}`}
                  className="rounded-sm border border-zinc-800 bg-[#0C0C0C] p-6 hover:border-zinc-700 transition-colors duration-75"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${m.core ? "bg-blue-600/20 text-blue-400" : "bg-zinc-900 text-zinc-400"}`}>
                      <m.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">{m.code}</span>
                        {m.core && <span className="font-mono text-[9px] uppercase tracking-wider text-blue-400 border border-blue-600/30 rounded-sm px-1.5 py-0.5">Core</span>}
                        {m.exp && <span className="font-mono text-[9px] uppercase tracking-wider text-amber-400 border border-amber-600/30 rounded-sm px-1.5 py-0.5">Uses an export</span>}
                      </div>
                      <h3 className="font-display text-base font-bold tracking-tight mt-1">{m.title}</h3>
                      <p className="text-sm text-zinc-500 mt-0.5">{m.blurb}</p>

                      <ol className="mt-4 space-y-2">
                        {m.steps.map((s, i) => (
                          <li key={i} className="flex gap-3 text-sm text-zinc-300">
                            <span className="font-mono text-[11px] text-zinc-600 mt-0.5 w-4 shrink-0">{i + 1}.</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>

                      {m.extras && (
                        <div className="mt-4 space-y-3 border-t border-zinc-800/80 pt-4">
                          {m.extras.map((e) => (
                            <div key={e.h}>
                              <div className="font-mono text-[11px] uppercase tracking-wider text-blue-400 mb-1">{e.h}</div>
                              <p className="text-sm text-zinc-400 leading-relaxed">{e.p}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <Link
                        to={m.to}
                        data-testid={`help-open-${m.id}`}
                        className="inline-flex items-center gap-1.5 mt-4 text-[12px] font-mono uppercase tracking-wider text-blue-400 hover:text-blue-300 transition-colors duration-75 print:hidden"
                      >
                        Open {m.title.split(" ")[0]} <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-12 text-zinc-500 font-mono text-sm">No modules match “{q}”.</div>
              )}
            </div>
          </section>

          {/* Checkpoints */}
          <section id="checkpoints" className="scroll-mt-24">
            <div className="flex items-center gap-2 mb-5">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <h2 className="font-display text-lg font-bold tracking-tight">Quality checkpoints (sign-off)</h2>
            </div>
            <div className="rounded-sm border border-zinc-800 overflow-hidden">
              {CHECKPOINTS.map(([step, crit], i) => (
                <div key={step} className={`grid grid-cols-[160px_1fr] gap-4 px-5 py-3 text-sm ${i % 2 ? "bg-[#0C0C0C]" : "bg-[#0A0A0A]"}`}>
                  <div className="font-mono text-[12px] text-zinc-400 uppercase tracking-wide">{step}</div>
                  <div className="text-zinc-300">{crit}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshooting" className="scroll-mt-24">
            <div className="flex items-center gap-2 mb-5">
              <Wrench className="w-5 h-5 text-zinc-400" />
              <h2 className="font-display text-lg font-bold tracking-tight">Troubleshooting</h2>
            </div>
            <div className="grid gap-3">
              {TROUBLESHOOTING.map(([sym, act]) => (
                <div key={sym} className="rounded-sm border border-zinc-800 bg-[#0C0C0C] p-4">
                  <div className="text-sm font-medium text-zinc-200">{sym}</div>
                  <div className="text-sm text-zinc-500 mt-1">{act}</div>
                </div>
              ))}
            </div>
          </section>

          <footer className="border-t border-zinc-800 pt-6 font-mono text-[11px] text-zinc-600">
            ADAC Suite · WI-ADAC-001 Rev 1.0 · For surveyors, civil/drafting staff and QA reviewers.
          </footer>
        </div>
      </div>
    </div>
  );
}
